// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0
// CPC override: prefer CPC Wallet extension; disable dev burner wallet.

import { SuiClientProvider, WalletProvider } from '@mysten/dapp-kit';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Fragment } from 'react';
import { Toaster, resolveValue, type ToastType } from 'react-hot-toast';
import { Outlet, ScrollRestoration } from 'react-router-dom';

import { KioskClientProvider } from '@mysten/core/src/components/KioskClientProvider';
import { CPC_WALLET_NAME } from '~/components/cpc/CpcConnectButton';
import { CpcWalletDiscovery } from '~/components/cpc/CpcWalletDiscovery';
import { NetworkContext, useNetwork } from '~/context';
import { LocaleProvider } from '~/i18n/LocaleContext';
import { Banner, type BannerProps } from '~/ui/Banner';
import {
	NetworkConfigs,
	createSuiClient,
	type Network,
} from '~/utils/api/DefaultRpcClient';

const toastVariants: Partial<Record<ToastType, BannerProps['variant']>> = {
	success: 'positive',
	error: 'error',
};

export function Layout() {
	const [network, setNetwork] = useNetwork();

	return (
		<LocaleProvider>
			<Fragment key={network}>
				<ScrollRestoration />
				<SuiClientProvider
					networks={NetworkConfigs}
					createClient={createSuiClient}
					network={network as Network}
					onNetworkChange={setNetwork}
				>
					<WalletProvider
						autoConnect
						enableUnsafeBurner={false}
						preferredWallets={[CPC_WALLET_NAME, 'CPC Wallet']}
						storageKey="cpc-explorer:wallet-connection"
					>
						<CpcWalletDiscovery />
						<KioskClientProvider>
							<NetworkContext.Provider value={[network, setNetwork]}>
								<Outlet />
								<Toaster
									position="bottom-center"
									gutter={8}
									containerStyle={{
										top: 40,
										left: 40,
										bottom: 40,
										right: 40,
									}}
									toastOptions={{
										duration: 4000,
									}}
								>
									{(toast) => (
										<Banner shadow border variant={toastVariants[toast.type]}>
											{resolveValue(toast.message, toast)}
										</Banner>
									)}
								</Toaster>
								<ReactQueryDevtools />
							</NetworkContext.Provider>
						</KioskClientProvider>
					</WalletProvider>
				</SuiClientProvider>
			</Fragment>
		</LocaleProvider>
	);
}
